import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { clone as cloneSkeleton } from "three/examples/jsm/utils/SkeletonUtils";
import AvatarViewportLoader from "../../bodyMap/AvatarViewportLoader";
import ErrorBoundary from "../../common/ErrorBoundary";
import { fetchPlayerMuscles } from "./v2OverviewApi";
import { V2_ZONES } from "./v2OverviewUtils";
import { mapMuscleSnapshot } from "./v2PriorityUtils";

const FBXViewer = lazy(() => import("../../bodyMap/FBXViewer"));

export default function V2CompactTwin({
  player,
  clubId,
  date,
  period,
  gltf,
  status,
  onMuscleSelect,
  onClearMuscle,
  selectionResetToken,
}) {
  const { t } = useTranslation();
  const pointerRef = useRef({ x: 0, y: 0 });
  const viewportRef = useRef(null);
  const playerRef = useRef(player);
  playerRef.current = player;
  const [visible, setVisible] = useState(false);
  const [muscleData, setMuscleData] = useState(() => mapMuscleSnapshot(null, player));
  const [musclesReady, setMusclesReady] = useState(false);
  const playerId = player?.id;
  const simulationId = player?.simulationId;

  useEffect(() => {
    const node = viewportRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return undefined;
    let active = true;
    setMusclesReady(false);
    if (!clubId || !playerId || !date) {
      setMuscleData(mapMuscleSnapshot(null, playerRef.current));
      setMusclesReady(true);
      return undefined;
    }
    fetchPlayerMuscles(clubId, playerId, { date, period })
      .then((payload) => {
        if (!active) return;
        setMuscleData(mapMuscleSnapshot(payload, playerRef.current));
        setMusclesReady(true);
      })
      .catch(() => {
        if (!active) return;
        setMuscleData(mapMuscleSnapshot(null, playerRef.current));
        setMusclesReady(true);
      });
    return () => {
      active = false;
    };
  }, [clubId, date, period, playerId, simulationId, visible]);

  const clonedModel = useMemo(() => {
    if (!visible || !gltf?.scene) return null;
    return { ...gltf, scene: cloneSkeleton(gltf.scene) };
  }, [gltf, player.id, visible]);

  return (
    <div className="my-1 mb-3" ref={viewportRef}>
      <div
        className="relative h-[248px] overflow-hidden rounded-[10px] bg-transparent"
        onPointerDown={(event) => {
          pointerRef.current = { x: event.clientX, y: event.clientY };
        }}
      >
        {status === "ready" && clonedModel ? (
          <ErrorBoundary fallback={<p className="m-0 grid h-full place-items-center text-center text-[#8f8abf]">{t("v2.overview.priority.twin_error")}</p>}>
            <Suspense fallback={<AvatarViewportLoader label={t("v2.overview.priority.twin_loading")} />}>
              <FBXViewer
                key={`v2-twin-${player.id}`}
                playerId={player.id}
                simulation={player.simulationId || date || player.id}
                fbxModel={clonedModel}
                prefetchedMuscleData={muscleData}
                prefetchedMuscleDataReady={musclesReady}
                autoSelectTrainability
                parameter="Trainability"
                transparentBackground
                showAura={false}
                showParameterBar={false}
                enableMuscleSelection
                compactControls
                fitCamera
                cameraDistance={10.4}
                isDarkMode
                showHoverTooltip={false}
                trainabilityPalette="v2"
                clearSelectionToken={selectionResetToken}
                onMuscleSelect={(muscle) => {
                  if (muscle) onMuscleSelect?.(muscle, pointerRef.current);
                  else onClearMuscle?.();
                }}
              />
            </Suspense>
          </ErrorBoundary>
        ) : status === "error" ? (
          <p className="m-0 grid h-full place-items-center text-center text-[#8f8abf]">
            {t("v2.overview.priority.twin_unavailable")}
          </p>
        ) : (
          <AvatarViewportLoader label={t("v2.overview.priority.twin_loading")} />
        )}
      </div>
      <div className="mt-1.5 flex items-center justify-between text-[11px] font-bold uppercase tracking-[0.05em] text-[#8f8abf]">
        <span>{t("v2.overview.priority.front")}</span>
        <small className="font-medium normal-case tracking-normal">{t("v2.overview.priority.rotate_hint")}</small>
        <span>{t("v2.overview.priority.back")}</span>
      </div>
      <ul className="mt-2.5 flex list-none flex-wrap gap-x-3 gap-y-1.5 p-0">
        {V2_ZONES.map((zone) => (
          <li key={zone.key} className="inline-flex items-center gap-1.5 text-[10px] text-[#9b96c9]">
            <i className="h-[7px] w-[7px] shrink-0 rounded-full" style={{ background: zone.color }} />
            {t(`v2.overview.${zone.key}`)}
          </li>
        ))}
      </ul>
    </div>
  );
}
